import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { ArrowLeft, Target, Heart, Eye, Users, Leaf, Handshake, Award, MapPin, ShoppingBasket } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const QuemSomos = () => {
  const { siteSettings } = useContext(AuthContext);
  const siteName = siteSettings?.site_name || 'Nossa Feira';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
          
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <ShoppingBasket className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Quem Somos</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Conectamos produtores rurais diretamente aos consumidores, promovendo o desenvolvimento sustentável e fortalecendo a economia local em {siteSettings?.contact_address || 'Bom Jesus da Lapa, BA'} e região.
            </p>
          </div>
        </div>

        {/* Nossa História */}
        <Card className="card-feira mb-8">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Nossa História</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  O <strong>{siteName}</strong> nasceu da necessidade de encurtar a distância entre quem produz e quem consome alimentos frescos e de qualidade em nossa região. Percebemos que muitos produtores rurais locais tinham dificuldades para comercializar seus produtos diretamente, enquanto consumidores buscavam alimentos mais frescos e com origem conhecida.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Nossa plataforma digital foi desenvolvida para ser uma ponte tecnológica que mantém a essência humana do relacionamento direto entre produtor e consumidor, valorizando a confiança, a transparência e o desenvolvimento mútuo.
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-orange-100 rounded-xl p-6">
                <div className="flex items-center justify-center h-48">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{siteSettings?.contact_address || 'Bom Jesus da Lapa, BA'}</h3>
                    <p className="text-gray-600 text-sm">Fortalecendo nossa comunidade local</p>
                    <Badge className="mt-2 bg-green-100 text-green-700">Bahia, Brasil</Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Missão, Visão e Valores */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          
          {/* Missão */}
          <Card className="card-feira h-full">
            <CardHeader className="text-center pb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-blue-600">Nossa Missão</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-700 leading-relaxed">
                Conectar produtores rurais locais diretamente aos consumidores através de uma plataforma digital simples e eficiente, promovendo o desenvolvimento sustentável, a economia solidária e o acesso a alimentos frescos e de qualidade.
              </p>
            </CardContent>
          </Card>

          {/* Visão */}
          <Card className="card-feira h-full">
            <CardHeader className="text-center pb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Eye className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-purple-600">Nossa Visão</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-700 leading-relaxed">
                Ser referência nacional em conectividade entre produtores rurais e consumidores, transformando-se na principal plataforma de comércio justo e sustentável, fortalecendo comunidades rurais em todo o Brasil.
              </p>
            </CardContent>
          </Card>

          {/* Valores */}
          <Card className="card-feira h-full">
            <CardHeader className="text-center pb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-green-600">Nossos Valores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700"><strong>Transparência:</strong> Informações claras sobre produtos e produtores</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700"><strong>Sustentabilidade:</strong> Práticas ambientalmente responsáveis</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700"><strong>Comunidade:</strong> Fortalecimento dos laços locais</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-gray-700"><strong>Qualidade:</strong> Produtos frescos e de excelência</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* O que fazemos */}
        <Card className="card-feira mb-8">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-gray-900 mb-4">O que Fazemos</CardTitle>
            <p className="text-gray-600">Facilitamos conexões que geram valor para toda a comunidade</p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
              
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Conectamos Pessoas</h4>
                <p className="text-sm text-gray-600">Aproximamos produtores e consumidores através de uma plataforma intuitiva</p>
              </div>

              <div className="text-center p-6 bg-orange-50 rounded-xl">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Promovemos Sustentabilidade</h4>
                <p className="text-sm text-gray-600">Incentivamos práticas agrícolas sustentáveis e consumo consciente</p>
              </div>

              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Facilitamos Comércio</h4>
                <p className="text-sm text-gray-600">Criamos um ambiente seguro e confiável para transações diretas</p>
              </div>

              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Valorizamos Qualidade</h4>
                <p className="text-sm text-gray-600">Garantimos padrões de qualidade através de aprovação criteriosa</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nosso Compromisso */}
        <Card className="card-feira mb-8">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Nosso Compromisso</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Estamos comprometidos em criar um ecossistema próspero para todos os envolvidos
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Para os Produtores</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Oferecemos uma plataforma gratuita e fácil de usar para divulgar seus produtos, alcançar novos clientes e obter renda justa pelo seu trabalho.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBasket className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Para os Consumidores</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Proporcionamos acesso a produtos frescos, de qualidade e com origem conhecida, além de apoiar a economia local da nossa região.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Para a Comunidade</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Contribuímos para o desenvolvimento sustentável, fortalecemos a economia local e promovemos práticas ambientalmente responsáveis.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Call to Action */}
        <Card className="card-feira">
          <CardContent className="p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Faça Parte da Nossa História</h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Junte-se a nós nessa jornada de transformação da agricultura local. Seja consumidor ou interessado em ser parceiro produtor, você é bem-vindo ao {siteName}!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/marketplace"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Explorar Produtos
              </Link>
              <Link 
                to="/register"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Cadastrar-se
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-2">Tem dúvidas ou sugestões? Entre em contato conosco!</p>
          <p className="text-green-600 font-medium">{siteSettings?.contact_email || 'contato@nossafeira.com'} • {siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}</p>
        </div>
      </div>
    </div>
  );
};

export default QuemSomos;